/** A field as far as the backend-name check cares: its own name, and the backend name it declares. */
export interface BackendFieldClaim {
  name: string;
  daoName?: string;
}

/**
 * Refuses a declaration in which two fields claim the same backend field.
 *
 * Throws where the metadata is built rather than letting the last declaration win at runtime:
 * the inverse mapping would have no way to decide which field a backend field belongs to, and
 * a silently dropped value is not recoverable once the object has been handed on.
 *
 * A field with no `daoName` claims the backend field of its own name, so it takes part in the
 * check too. Otherwise the one collision that actually loses data would pass: `label` declaring
 * `daoName('fileName')` beside a plain `fileName` leaves the inverse mapping knowing only
 * `fileName -> label`, so `fileName`'s value never reaches the backend at all.
 */
export function assertBackendFieldNamesAreUnique(fields: BackendFieldClaim[]): void {
  const declaredBy = new Map<string, string>();

  for (const field of fields) {
    const backendField = field.daoName ?? field.name;
    const previous = declaredBy.get(backendField);
    if (previous !== undefined) {
      throw new Error(
        `Backend field '${backendField}' is claimed by both '${previous}' and '${field.name}'. ` +
          'Two properties cannot share one backend field, because mapping back to the backend would be ambiguous. ' +
          'A property without a declared backend name claims the field of its own name.',
      );
    }
    declaredBy.set(backendField, field.name);
  }
}
